import { createSelector } from '@reduxjs/toolkit';

import FieldInformation from '@app/store/contract-progress/FieldInformation';
import FieldPosition from '@app/store/contract-progress/FieldPosition';
import FieldSize from '@app/store/contract-progress/FieldSize';
import { ContractProgressIdsInPagesState } from '@app/store/contract-progress/reducers/field/idsInPages';
import { ContractProgressFieldSelectedSignatureState } from '@app/store/contract-progress/reducers/field/selectedSignature';
import { FieldNodeForSequence } from '@app/store/contract-progress/reducers/tracker/sequence';
import FieldType from '@app/store/FieldType';
import FontType from '@app/store/FontType';
import {
  FieldType as TransportLayerFieldType,
  FontStyle,
  Input,
} from '@app/store/transport-layer/types/field';
import { WithId } from '@app/store/transport-layer/types/utility';
import { RootState } from '@app/store/types';

import CheckboxFieldInformation from './CheckboxFieldInformation';
import ContractProgressStep from './ContractProgressStep';
import SignatureFieldInformation from './SignatureFieldInformation';
import TextFieldInformation from './TextFieldInformation';

const getContractProgressState = (state: RootState) => state.contractProgress;

export const getFileUrl = (state: RootState) => getContractProgressState(state).file;

const getFieldState = (state: RootState) => getContractProgressState(state).field;

export const getCompletedFieldIds = (state: RootState) => getFieldState(state).completedIds;

const getFieldIdsState = (state: RootState) => getFieldState(state).ids;

const getCheckboxGroupIdsState = (state: RootState) => getFieldIdsState(state).checkboxGroups;

export const getRequiredCheckboxGroupIds = (state: RootState) =>
  getCheckboxGroupIdsState(state).required;

export const getOptionalCheckboxGroupIds = (state: RootState) =>
  getCheckboxGroupIdsState(state).optional;

const getTextFieldIdsState = (state: RootState) => getFieldIdsState(state).textFields;

export const getRequiredTextFieldIds = (state: RootState) => getTextFieldIdsState(state).required;

export const getOptionalTextFieldIds = (state: RootState) => getTextFieldIdsState(state).optional;

export const getSignatureFieldIds = (state: RootState) => getFieldIdsState(state).signatureFields;

export const getFieldIdsInPages = (state: RootState): ContractProgressIdsInPagesState =>
  getFieldState(state).idsInPages;

export const getFieldInformation = (state: RootState) => getFieldState(state).information;

export const getFieldPositions = (state: RootState) => getFieldState(state).positions;

export const getFieldSizes = (state: RootState) => getFieldState(state).sizes;

export const getSelectedSignature = (
  state: RootState
): ContractProgressFieldSelectedSignatureState => getFieldState(state).selectedSignature;

export const getPdfArrayBuffer = (state: RootState) =>
  getContractProgressState(state).pdfArrayBuffer;

export const getPropsId = (state: RootState, props: { id: string }) => props.id;

export const getSpecificFieldInformation = (
  state: RootState,
  props: { id: string }
): FieldInformation | null => getFieldInformation(state)[props.id] ?? null;

export const getSpecificFieldPosition = (
  state: RootState,
  props: { id: string }
): FieldPosition | null => getFieldPositions(state)[props.id] ?? null;

export const getSpecificFieldSize = (state: RootState, props: { id: string }): FieldSize | null =>
  getFieldSizes(state)[props.id] ?? null;

export const getContractProgressCompleteLoadingStatus = (state: RootState) =>
  getContractProgressState(state).completeLoadingStatus;

const getContractProgressStep = (state: RootState) => getContractProgressState(state).step;

export const getContractProgressOrder = (state: RootState) => getContractProgressStep(state).order;

export const getContractStep = (state: RootState): ContractProgressStep | null => {
  const { order, currentStepIndex } = getContractProgressStep(state);
  return order[currentStepIndex] || null;
};

export const getIsFirstContractProgressStep = createSelector(
  getContractProgressStep,
  ({ currentStepIndex }) => currentStepIndex === 0
);

/**
 * 다음 단계로 넘어갈 때 서명 요청을 해야하는지 여부를 반환합니다.
 */
export const getIsContractProgressSubmitStep = createSelector(
  getContractProgressStep,
  ({ order, currentStepIndex }) => order[currentStepIndex + 1] === ContractProgressStep.COMPLETE
);

export const getTermOfUseUrl = (state: RootState) => getContractProgressState(state).termOfUseUrl;

const getTrackerState = (state: RootState) => getContractProgressState(state).tracker;

export const getInitialTrackedId = (state: RootState) => getTrackerState(state).initialTrackedId;

export const getTrackerFocusTargetId = (state: RootState) => getTrackerState(state).focusTargetId;

export const getTrackerSequence = (state: RootState) => getTrackerState(state).sequence;

export const getTrackedFieldId = (state: RootState) => getTrackerState(state).trackedFieldId;

export const isTrackerWithinBoundaries = (state: RootState) =>
  getTrackerState(state).withinBoundaries;

export const getAuthenticationKey = (state: RootState) =>
  getContractProgressState(state).authenticationKey;
export const getDocumentId = (state: RootState) => getContractProgressState(state).documentId;

const getParticipantState = (state: RootState) => getContractProgressState(state).participant;
export const getParticipantId = (state: RootState) => getParticipantState(state)?.participantId;
export const getParticipantName = (state: RootState) => getParticipantState(state)?.name;

export const isLastOrder = (state: RootState) => getContractProgressState(state).isLastOrder;
export const getIsLastInPersonSigner = (state: RootState) =>
  getContractProgressState(state).isLastInPersonSigner;

export const isNeedFirstFocus = (state: RootState) =>
  getContractProgressState(state).isNeedFirstFocus;
export const isLoading = (state: RootState) => getContractProgressState(state).isLoading;
export const getParticipantUserType = (state: RootState) =>
  getContractProgressState(state).participantUserType;
export const getTabKeyCount = (state: RootState) => getContractProgressState(state).tabKeyCount;

/**
 * 특정 페이지에 존재하는 field 아이디 List를 반환한다.
 */
export const getFieldIdsInPage = (state: RootState, props: { pageNumber: number }) => {
  const idsInPages = getFieldIdsInPages(state);
  if (idsInPages) {
    const { pageNumber } = props;
    return pageNumber in idsInPages ? idsInPages[pageNumber] : null;
  }
  return null;
};

/**
 * 각 페이지에 필드 존재 여부를 반환한다.
 */
export const makeGetPageFieldExist = createSelector(getFieldIdsInPage, (idsInPage) => !!idsInPage);

/**
 * 각 페이지마다 입력이 필요한 모든 필수 필드의 입력 완료 여부를 반환한다.
 */
export const makeGetPageFieldCompleted = createSelector(
  getFieldIdsInPage,
  getCompletedFieldIds,
  getOptionalTextFieldIds,
  getOptionalCheckboxGroupIds,
  (fieldIdsInPage, completedIds, textFieldOptionalIds, checkboxGroupOptionIds): boolean => {
    if (!fieldIdsInPage) {
      return false;
    }

    return fieldIdsInPage
      .filter((id) => !textFieldOptionalIds.includes(id))
      .filter((id) => !checkboxGroupOptionIds.includes(id))
      .every((id) => completedIds.includes(id));
  }
);

/**
 * 사인도장 필드 아이디 리스트를 반환한다.
 */
export const getSignatureIds = createSelector(getFieldInformation, (information) =>
  Object.values(information)
    .filter((field) => field.type === FieldType.SIGNATURE_FIELD)
    .map((field) => field.id)
);

/**
 * 입력 완료된 서명 아이디 리스트를 반환한다.
 */
export const getCompletedSignatureIds = createSelector(
  getSignatureIds,
  getCompletedFieldIds,
  (signatureIds, completedIds) => completedIds.filter((id) => signatureIds.includes(id))
);

/**
 *  입력이 완료된 입력 필수 필드 개수를 반환한다.

 */
export const getCompletedFieldCount = createSelector(
  getCompletedFieldIds,
  getOptionalCheckboxGroupIds,
  getOptionalTextFieldIds,
  (completedIds, checkboxGroupOptionIds, optionalTextFieldIds) =>
    completedIds
      .filter((id) => !checkboxGroupOptionIds.includes(id))
      .filter((id) => !optionalTextFieldIds.includes(id)).length
);

/**
 * 선택 입력항목인 필드의 총 개수를 반한한다.
 */
export const getOptionalFieldCount = createSelector(
  getOptionalCheckboxGroupIds,
  getOptionalTextFieldIds,
  (optionalCheckboxFieldIds, optionalTextFieldIds) =>
    optionalCheckboxFieldIds.length + optionalTextFieldIds.length
);

/**
 * 필수 입력항목인 필드의 총 개수를 반환한다.
 */
export const getRequiredFieldCount = createSelector(
  getSignatureFieldIds,
  getRequiredCheckboxGroupIds,
  getRequiredTextFieldIds,
  (signatureIds, requiredCheckboxFieldIds, requiredTextFieldIds) =>
    signatureIds.length + requiredCheckboxFieldIds.length + requiredTextFieldIds.length
);

/**
 * index를 기준으로 잘라서 앞부분을 뒤부분으로 이동하여 새로운 배열을 반환합니다.
 * (원본 배열은 변경하지 않습니다.)
 */
function rotateArrayByIndex(array: FieldNodeForSequence[], index: number) {
  if (index && array.length > 1) {
    const front = array.slice(0, index);
    const end = array.slice(index);
    return end.concat(front);
  }
  return array;
}

function findSeqIndex(array: FieldNodeForSequence[], id: string): number {
  return array.findIndex((current) => {
    return (
      current.id === id ||
      (current.type === 'CHECKBOX_GROUP' && findSeqIndex(current.children, id) !== -1)
    );
  });
}

function getSeqNode(array: FieldNodeForSequence[], id: string) {
  const index = findSeqIndex(array, id);
  if (index === -1) {
    return null;
  }
  return array[index];
}

/**
 * 추적하고 있는 필드의 위치 정보를 반환한다.
 */
export const getTrackedFieldPosition = createSelector(
  getTrackedFieldId,
  getFieldPositions,
  (trackedId, positions) => {
    if (!trackedId) {
      return null;
    }
    return positions[trackedId];
  }
);

/**
 * 추적하고 있는 필드의 상대적인 pageY를 반환한다.
 */
export const getTrackedFieldPageY = createSelector(getTrackedFieldPosition, (position) => {
  if (!position) {
    return 0;
  }
  return position.initialRelativePageY;
});

// TODO(#5647): Next tracker 관련 테스트 코드가 필요합니다.
/**
 * 다음 차례로 추적해야할 필드의 id를 반환한다.
 * 입력 해야할 필수 입력 필드가 하나만 남을 경우 해당 필드의 아이디를 반환한다.
 */
export const getNextTrackedFieldId = createSelector(
  getCompletedFieldIds,
  getTrackedFieldId,
  getTrackerSequence,
  (completedIds, trackedId, trackerSequence) => {
    if (trackedId) {
      const nextFieldIds = rotateArrayByIndex(
        trackerSequence,
        findSeqIndex(trackerSequence, trackedId)
      )
        .filter((node) => {
          const target = getSeqNode(trackerSequence, node.id);
          return target ? !completedIds.includes(target.id) : false;
        })
        .map((node) => {
          if (node.type === 'CHECKBOX_GROUP') {
            return node.children[0].id;
          }
          return node.id;
        });

      const trackedNode = getSeqNode(trackerSequence, trackedId);

      if (nextFieldIds.length === 0 || completedIds.length === trackerSequence.length) {
        return null;
      }
      const isCompleted = !!trackedNode && completedIds.includes(trackedNode.id);

      return isCompleted || nextFieldIds.length === 1 ? nextFieldIds[0] : nextFieldIds[1];
    }
    return null;
  }
);

/**
 * 다음 차례로 추적해야할 필드의 위치 정보를 반환한다.
 */
export const getNextTrackedFieldPosition = createSelector(
  getNextTrackedFieldId,
  getFieldPositions,
  (nextTrackedId, positions) => {
    if (!nextTrackedId) {
      return null;
    }
    return positions[nextTrackedId];
  }
);

/**
 * 다음 차례로 추적해야할 필드의 상대적인 pageY를 반환한다.
 */
export const getNextTrackedFieldPageY = createSelector(getNextTrackedFieldPosition, (position) => {
  if (!position) {
    return 0;
  }
  return position.initialRelativePageY;
});

/**
 * 트래킹 종료 여부를 반환한다. 해당 불리언 값은 서명 전체 완료와 동일한 의미를 가진다.
 */
export const getTrackingIsEnd = createSelector(
  getCompletedFieldIds,
  getTrackerSequence,
  (completed, sequence) => completed.length === sequence.length
);

/**
 * 통합으로 선언된 필드 타입을 재활용 하는 방향을 고려했지만,
 * 백엔드의 요구사항 중 validation을 위해 타입이 추가되어야 하고[1],
 * 이 타입이 없으면 타입을 조회하기 위해 validation 이전에 database에 엑세스 해야하는 문제가 발생합니다.
 * 따라서 type를 추가한 input을 선언합니다.
  [1] https://www.notion.so/modusign/bb8e24e723434ecc946dfecb85db5ee3#48ead512bb6e44638e07f6b6d623511b
 */
export type InputTypeForContractSigning = WithId<Input> & { type: TransportLayerFieldType };

/**
 * 계약 서명을 위해 서명 필드에 대한 정보를 반환한다.
 * @deprecated (standardize-document-content) 렌더러 정리 후 선택자 개선하기
 */
export const getSignatureFieldValues = createSelector(
  getFieldInformation,
  getFieldPositions,
  getFieldSizes,
  getSelectedSignature,
  (information, positions, sizes, signature) =>
    Object.keys(information)
      .filter((key) => information[key].type === FieldType.SIGNATURE_FIELD)
      .map<InputTypeForContractSigning>((key) => {
        const fieldInformation = information[key] as SignatureFieldInformation;
        return {
          id: fieldInformation.id,
          type: 'SIGNATURE',
          position: {
            x: positions[fieldInformation.id].currentRelativeOffsetX,
            y: positions[fieldInformation.id].currentRelativeOffsetY,
            z: 0,
            page: positions[fieldInformation.id].page,
          },
          size: {
            width: sizes[fieldInformation.id].relativeWidth,
            height: sizes[fieldInformation.id].relativeHeight,
          },
          overrideConfig: {
            required: true,
            allow: fieldInformation.allowSignatureTypes.map((type) => type),
            disabled: false,
            changeableAttributes: [],
            valueType: 'SIGNATURE',
          },
          value: signature ? signature.id.toString() : null,
        };
      })
);

/**
 * 계약 서명을 위해 체크박스 필드에 대한 정보를 반환한다.
 * @deprecated (standardize-document-content) 렌더러 정리 후 선택자 개선하기
 */
export const getCheckboxFieldValues = createSelector(
  getFieldInformation,
  getFieldPositions,
  getFieldSizes,
  (information, positions, sizes) =>
    Object.keys(information)
      .filter((key) => information[key].type === FieldType.CHECKBOX_FIELD)
      .filter((key) => {
        const fieldInformation = information[key];
        if (fieldInformation.type === FieldType.CHECKBOX_FIELD) {
          return fieldInformation.checked;
        }
        return false;
      })
      .map<InputTypeForContractSigning>((key) => {
        const value = information[key] as CheckboxFieldInformation;
        return {
          id: value.id,
          type: 'CHECKBOX',
          position: {
            x: positions[value.id].currentRelativeOffsetX,
            y: positions[value.id].currentRelativeOffsetY,
            z: 0,
            page: positions[value.id].page,
          },
          size: {
            width: sizes[value.id].relativeWidth,
            height: sizes[value.id].relativeHeight,
          },
          overrideConfig: {
            required: false,
            disabled: false,
            changeableAttributes: [],
            valueType: 'BOOLEAN',
          },
          value: 'TRUE',
        };
      })
);

/**
 * 계약 서명을 위해 텍스트 필드에 대한 정보를 반환한다.
 * @deprecated (standardize-document-content) 렌더러 정리 후 선택자 개선하기
 */
export const getTextFieldValues = createSelector(
  getFieldInformation,
  getFieldPositions,
  getFieldSizes,
  (information, positions, sizes) =>
    Object.keys(information)
      .filter((key) => information[key].type === FieldType.TEXT_FIELD)
      .filter((key) => {
        const fieldInformation = information[key];
        return fieldInformation.type === FieldType.TEXT_FIELD && !!fieldInformation.value;
      })
      .map<InputTypeForContractSigning>((key) => {
        const fieldInformation = information[key] as TextFieldInformation;
        return {
          id: fieldInformation.id,
          type: 'TEXT',
          position: {
            page: positions[fieldInformation.id].page,
            x: positions[fieldInformation.id].currentRelativeOffsetX,
            y: positions[fieldInformation.id].currentRelativeOffsetY,
            z: 0,
          },
          size: {
            height: sizes[fieldInformation.id].relativeHeight,
            width: sizes[fieldInformation.id].relativeWidth,
          },
          overrideConfig: {
            required: fieldInformation.required,
            disabled: false,
            changeableAttributes: [],
            valueType: 'STRING',
            fontStyle: {
              size: fieldInformation.currentFontSize,
              type: fieldInformation.fontFamily === FontType.NOTO_SANS ? 'NOTO_SANS' : 'NOTO_SERIF',
            } as FontStyle,
            textAlign: fieldInformation.textAlign,
            defaultInputValue: null,
          },
          value: fieldInformation.value,
        };
      })
);

/**
 * 계약 서명을 위해 첨부 파일에 대한 전체 정보를 반환한다.
 */
export const getContractProgressAttachments = (state: RootState) =>
  getContractProgressState(state).attachments;

/**
 * 첨부 정보중 현재 서명진행에 필요한 서명자의 요청만을 반환한다.
 */
export const getContractProgressAttachmentsByParticipantId = createSelector(
  getContractProgressAttachments,
  getParticipantId,
  (attachments, participantId) =>
    attachments.filter((item) => item.participant.participantId === participantId)
);

/**
 * 현재 서명자의 첨부파일 정보, 수
 */
export const getContractProgressAttachmentsCount = createSelector(
  getContractProgressAttachmentsByParticipantId,
  (attachments) => attachments.length
);

/**
 * 현재 서명자의 첨부파일 정보, 요청정보(첨부유형, 메세지, 필수여부)
 */
export const getContractProgressAttachmentsRequests = createSelector(
  getContractProgressAttachmentsByParticipantId,
  (attachments) => attachments.map((attachment) => attachment.request)
);

/**
 * 현재 서명자의 첨부파일 요청,필수요청 수
 */
export const getContractProgressAttachmentsRequiredFileCount = createSelector(
  getContractProgressAttachmentsByParticipantId,
  (attachments) => {
    let count = 0;
    attachments.forEach((attachment) => {
      const { request } = attachment;
      if (request.required) {
        count += 1;
      }
    });
    return count;
  }
);

/**
 * 현재 서명자의 첨부파일 요청,필수요청 중 업로드한 수
 */
export const getContractProgressAttachmentsRequiredFileUploadCount = createSelector(
  getContractProgressAttachmentsByParticipantId,
  (attachments) => {
    let count = 0;
    attachments.forEach((attachment) => {
      const { request } = attachment;
      if (request.required && attachment.file) {
        count += 1;
      }
    });
    return count;
  }
);

/**
 * 첨부 정보중 현재 서명진행에 필요한 서명자외 다른 서명자가 파일을 첨부했는지 여부(true/false)
 */
export const isOtherParticipantUploaded = createSelector(
  getContractProgressAttachments,
  getParticipantId,
  (attachments, participantId) => {
    const others = attachments
      .filter((item) => item.participant.participantId !== participantId)
      .filter((item) => item.file !== null);
    return others.length > 0;
  }
);

/**
 * 현재 파일을 업로드 중인지 확인하는 상태값
 */
export const getIsAttaching = (state: RootState) => getContractProgressState(state).isAttaching;

export const getRequester = (state: RootState) => getContractProgressState(state).requester;

export const getHost = (state: RootState) => getContractProgressState(state).host;

export const getContractProgressDocumentName = (state: RootState) =>
  getContractProgressState(state).name;
